<template>
  <div>
    <HostListFilters />

    <v-card outlined>
      <v-data-table
        disable-sort
        hide-default-footer
        mobile-breakpoint="20"
        :headers="headers"
        :items="hosts"
        :loading="isLoading"
        :items-per-page="itemsPerPage"
        :no-data-text="$t('table.noResults')"
        :loading-text="$t('table.loading')"
        @dblclick:row="showHostRow"
        class="clickable"
      >
        <template v-slot:[`item.phone`]="{ item }">
          <span>
            {{ formatPhone(item.phone) }}
          </span>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            small
            :class="{ primary: item.enabled }"
            v-if="!item.suspended"
          >
            {{
              item.enabled ? $t('host.label.active') : $t('host.label.blocked')
            }}
          </v-chip>
          <v-chip small class="orange lighten-3" v-else>
            {{ $t('host.label.suspended') }}
          </v-chip>
        </template>

        <template v-slot:[`item.started_onboarding_training_at`]="{ item }">
          <span>
            {{
              item.started_onboarding_training_at
                ? formatDate(item.started_onboarding_training_at)
                : '---'
            }}
          </span>
        </template>

        <template v-slot:[`item.id`]="{ item }">
          <div class="d-flex align-center">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  :class="{ linked: item.asaas_account }"
                  :style="`background-image: url(${logoAsaas}`"
                  :alt="item.asaas_account ? $t('host.label.asaasLinked') : $t('host.label.asaasNotLinked')"
                  class="asaas"
                />
              </template>
              <span>
                {{ item.asaas_account ? $t('host.label.asaasLinked') : $t('host.label.asaasNotLinked')}}
              </span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="asaas service-provider-icon"
                  :class="{ 'service-provider-icon--disabled': !isServiceProvider(item) }"
                >
                  <v-icon
                    :class="[
                      'service-provider-icon__mdi',
                      isServiceProvider(item)
                        ? 'service-provider-icon__mdi--active'
                        : 'service-provider-icon__mdi--disabled'
                    ]"
                  >
                    mdi-account-tie-outline
                  </v-icon>
                </div>
              </template>
              <span>
               {{
                 isServiceProvider(item)
                 ? $t('host.label.serviceProviderUser')
                 : $t('host.label.notServiceProviderUser')
               }}
              </span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  :class="{ linked: item.checklist_id }"
                  :style="`background-image: url(${logoChecklist}`"
                  :alt="item.checklist_id ? $t('customer.label.checklistLinked') : $t('customer.label.checklistNotLinked')"
                  class="asaas"
                />
              </template>
              <span>
                {{ item.checklist_id ? $t('host.label.checklistLinked') : $t('host.label.checklistNotLinked')}}
              </span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item
                v-if="!checkUserRole(['ROLE_SUPPORT'])"
                @click="openUpdateModal(item.id)"
              >
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-pencil
                    </v-icon>
                    <span class="body-2">Editar</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="checkUserRole(['ROLE_ADMIN', 'ROLE_HAUSEFUL_OPERATOR'])"
                @click="openImpersonateModal(item.id)"
              >
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-account-cowboy-hat
                    </v-icon>
                    <span class="body-2">{{$t('impersonate.action')}}</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <v-list-item @click="showHost(item.id)">
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-eye
                    </v-icon>
                    <span class="body-2">Visualizar</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.asaas_account" @click="openLinkAsaasHostModal(item.id)">
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-link-variant
                    </v-icon>
                    <span class="body-2">{{ $t('host.label.linkAsaas') }}</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <v-list-item v-else @click="openUnlinkAsaasHostModal(item.id)">
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-link-variant-off
                    </v-icon>
                    <span class="body-2">{{ $t('host.label.unlinkAsaas') }}</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.checklist_id" @click="openCreateHostOnChecklistModal(item.id)">
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-link-variant
                    </v-icon>
                    <span class="body-2">{{ $t('host.label.linkChecklist') }}</span>
                  </div>
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="!checkUserRole(['ROLE_SUPPORT'])"
                @click="openDeleteModal(item.id, item.name)"
              >
                <v-list-item-title>
                  <div class="d-flex align-center">
                    <v-icon
                      color="error"
                      style="transform: scale(0.85); opacity: 0.75"
                      class="mr-3 grey--text text--lighten-1"
                    >
                      mdi-delete
                    </v-icon>
                    <span class="error--text body-2">Deletar</span>
                  </div>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <RightDrawer
      :title="currentDrawerTitle"
      :moreInfo="onboardingDrawerOpened"
      :items="onboardingDrawerOpened ? onboardingMenuItems : []"
    >
      <HostSummary v-if="hostDrawerOpened" :data="host" />
      <HostOnboardingResume v-if="onboardingDrawerOpened" :host="host" />
    </RightDrawer>

    <DeleteModal
      :callback="fetchHosts"
      toDelete="deleteHost"
      ref="deleteHostModal"
    />

    <HostUpdate ref="updateHostModal" />
    <ModalImpersonate ref="impersonateModal" />
    <ModalHostOnboardingStepChange :host="host" next />
    <ModalReproveHostOnboarding :host="host" />
    <ModalLinkAsaasHost />
    <ModalUnlinkAsaasHost />
    <ModalCreateUserOnChecklist type="host" />

    <h-pagination :fetch="fetchHosts" />

    <HostCreate v-if="!checkUserRole(['ROLE_SUPPORT'])" ref="createHostModal" />

    <HostSpecialtiesModal ref="hostSpecialtiesModal" />
  </div>
</template>

<script>
import router from '@/router'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { hostHeaders } from './listHeaders/hostHeaders'
import { catalogDateWithoutTime, catalogPhone } from '@/util/catalog'
import HostListFilters from '@/components/host/HostListFilters'
import RightDrawer from '@/components/app/RightDrawer'
import HostSummary from '@/components/host/HostSummary'
import HostOnboardingResume from '@/components/host/HostOnboardingResume'
import HostCreate from '@/components/host/HostCreate'
import HostUpdate from '@/components/host/HostUpdateModal'
import DeleteModal from '@/components/app/DeleteModal'
import { handleParams } from '@/util/routes'
import { checkUserRole } from '@/util/helpers'
import ModalHostOnboardingStepChange from '@/components/host/modals/ModalHostOnboardingStepChange'
import ModalLinkAsaasHost from '@/components/host/modals/ModalLinkAsaasHost'
import ModalUnlinkAsaasHost from '@/components/host/modals/ModalUnlinkAsaasHost'
import ModalReproveHostOnboarding from '@/components/host/modals/ModalReproveHostOnboarding'
import logoAsaas from '@/assets/images/logo-asaas.svg'
import logoChecklist from '@/assets/images/logo-hauseful-checklist.svg'
import ModalImpersonate from '@/components/impersonate/ModalImpersonate.vue'
import ModalCreateUserOnChecklist from '@/components/host/modals/ModalCreateUserOnChecklist'
import HostSpecialtiesModal from '@/components/host/modals/HostSpecialtiesModal'

export default {
  name: 'HostList',

  components: {
    ModalImpersonate,
    HostListFilters,
    RightDrawer,
    HostSummary,
    HostCreate,
    HostUpdate,
    DeleteModal,
    HostOnboardingResume,
    ModalHostOnboardingStepChange,
    ModalReproveHostOnboarding,
    ModalLinkAsaasHost,
    ModalUnlinkAsaasHost,
    ModalCreateUserOnChecklist,
    HostSpecialtiesModal,
  },

  data() {
    return {
      title: router.currentRoute.name,
      headers: hostHeaders,
      activeDrawer: '',
      onboardingMenuItems: [],
      logoAsaas,
      logoChecklist,
    }
  },

  mounted() {
    this.fetchHosts()
    this.fetchHostOnboardingCoursePercentages()
    this.openHostDetails()
  },

  computed: {
    ...mapGetters(['host', 'hosts', 'isLoading', 'itemsPerPage']),

    onboardingDrawerOpened() {
      return this.activeDrawer == 'HOST_ONBOARDING'
    },

    hostDrawerOpened() {
      return this.activeDrawer == 'HOST'
    },

    currentDrawerTitle() {
      return this.onboardingDrawerOpened ? 'Onboarding Host' : this.$t('detailsModal.title')
    },
  },

  methods: {
    ...mapActions([
      'fetchHosts',
      'fetchHost',
      'fetchHostOnboardingStepData',
      'fetchHostBackgroundCheck',
      'openModal',
      'readHostOnboardingStepFiles',
      'fetchHostOnboardingCoursePercentages',
    ]),

    ...mapMutations(['setIsDrawerOpen']),

    formatPhone: catalogPhone,
    formatDate: catalogDateWithoutTime,

    async showHost(id) {
      this.activeDrawer = 'HOST'
      this.setIsDrawerOpen()
      await this.fetchHost(id)
      handleParams('hosts', id)
    },

    async showHostOnboarding(hostId, onboardingStepType) {
      this.activeDrawer = 'HOST_ONBOARDING'
      if (['DISAPPROVED', 'DONE'].includes(onboardingStepType)) {
        this.onboardingMenuItems = []
      } else {
        this.onboardingMenuItems = [
          {
            title: 'Aprovar passo',
            color: 'primary',
            action: this.openChangeHostOnboardingStep,
            icon: 'mdi-check',
          },
          {
            title: 'Reprovar candidato',
            color: 'error',
            icon: 'mdi-cancel',
            action: this.openReproveHostOnboardingModal,
          },
        ]
      }
      this.setIsDrawerOpen()
      await this.fetchHost(hostId)
      await this.fetchHostOnboardingStepData(hostId)
      await this.fetchHostBackgroundCheck(hostId)
      await this.readHostOnboardingStepFiles(hostId)
      handleParams('hosts', hostId)
    },

    showHostRow(event, { item }) {
      this.showHost(item.id)
    },

    openDeleteModal(hostId, hostName) {
      this.$refs.deleteHostModal.openModal(hostId, hostName)
    },

    openUpdateModal(hostId) {
      this.$refs.updateHostModal.openModal(hostId)
    },

    openImpersonateModal(hostId) {
      this.$refs.impersonateModal.openModal(hostId, 'host')
    },

    async openChangeHostOnboardingStep() {
      this.openModal('HOST_ONBOARDING_CHANGE_STEP')
    },

    openReproveHostOnboardingModal() {
      this.openModal('REPROVE_HOST_ONBOARDING')
    },

    openLinkAsaasHostModal(hostId) {
      handleParams('hosts', hostId)
      this.openModal('LINK_ASAAS_HOST_MODAL')
    },

    async openUnlinkAsaasHostModal(hostId) {
      handleParams('hosts', hostId)
      await this.fetchHost(hostId)
      this.openModal('UNLINK_ASAAS_HOST_MODAL')
    },

    async openCreateHostOnChecklistModal(hostId) {
      handleParams('hosts', hostId)
      await this.fetchHost(hostId)
      this.openModal('CREATE_USER_ON_CHECKLIST_MODAL')
    },

    openCreateHostModal() {
      const modal = this.$refs.createHostModal?.$refs.createModal
      if (modal) modal.openCreateModal()
    },

    openHostSpecialtiesModal() {
      this.$refs.hostSpecialtiesModal?.openModal()
    },

    openHostDetails() {
      const hostId = this.$route.query.id
      hostId && this.showHost(hostId)
    },

    checkUserRole,

    isServiceProvider(item) {
      return item && item.type === 'ROLE_SERVICE_PROVIDER'
    },
  },
}
</script>

<style scoped>
.pulse >>> .v-badge__badge {
  border-radius: 50%;
  animation: 1.5s pulse infinite;
}

@keyframes pulse {
  0% {
    box-shadow: #fb8c00 0 0 0 0;
  }
  100% {
    box-shadow: #fb8c0000 0 0 0 7px;
  }
}

.asaas {
  display: block;
  width: 22px;
  height: 22px;
  margin: 0 10px;
  border-radius: 50%;
  background-size: 20px;
  background-repeat: no-repeat;
  background-position: center 2px;
  background-color: #fff;
  box-shadow: 0 0 0 5px #fff;
}

.asaas.linked {
  background-position: center -32px;
}

.service-provider-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: none !important;
}

.service-provider-icon__mdi {
  font-size: 18px;
}

.service-provider-icon__mdi--active {
  color: #1e88e5;
}

.service-provider-icon__mdi--disabled {
  color: #bdbdbd;
}

.service-provider-icon--disabled {
  opacity: 0.4;
}
</style>
